import { useCallback, useEffect, type FC } from 'react'
import { useRouter } from 'next/router'

import { Badge } from '@wartek-id/badge'

import { Header } from 'app/KinerjaShared/components/Header'
import { InternalServerError } from 'utils/customError'
import { usePelaksanaanPraktikKinerjaPS } from 'app/KinerjaPSPegawaiPage/hooks/usePelaksanaanPraktikKinerjaPS'
import { PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import CardForm from 'app/KinerjaPSPegawaiPage/pages/Pelaksanaan/PraktikKinerja/DiskusiTindakLanjut/components/CardForm'
import OnceLoaded from 'app/KinerjaPSPegawaiPage/pages/Pelaksanaan/PraktikKinerja/components/OnceLoaded'
import CardFormPreview from 'app/KinerjaPSPegawaiPage/pages/Pelaksanaan/PraktikKinerja/PendampinganSatuanPendidikan/components/CardFormPreview'

import type {
  TPraktikKinerjaDiskusiTindakLanjutFormValue,
  TPraktikKinerjaFormName,
  TPraktikKinerjaPendampinganFormValue,
} from 'types/kinerja-ps-pegawai'

type DiskusiTindakLanjutProps = {}

const formNameToGet: TPraktikKinerjaFormName = 'pendampingan_sekolah'
const formNameToPost: TPraktikKinerjaFormName = 'diskusi_tindak_lanjut'

const DiskusiTindakLanjut: FC<DiskusiTindakLanjutProps> = () => {
  const router = useRouter()

  // Prepare form values from the API
  const formValues =
    usePelaksanaanPraktikKinerjaPS<TPraktikKinerjaPendampinganFormValue>({
      formName: formNameToGet,
    })

  const formValuesForPost =
    usePelaksanaanPraktikKinerjaPS<TPraktikKinerjaDiskusiTindakLanjutFormValue>(
      { formName: formNameToPost }
    )

  //  Check if the api call is successful, based on the formValuesForPost, since it holds the correct form name for this page
  const isSuccess = !formValuesForPost.isLoading && formValuesForPost.isSuccess

  const goBack = useCallback(() => {
    if (window.history.state.idx <= 1)
      router.push(
        `${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`
      )
    else router.back()
  }, [router])

  useEffect(() => {
    if (
      formValues.psSummaryHookResult.summaryFetchStatus === 'success' &&
      !formValuesForPost.isLoading &&
      !formValuesForPost.isSuccess
    ) {
      throw new InternalServerError(
        'API_PATH',
        new Error('Failed to fetch summary or form values')
      )
    }

    if (formValuesForPost.isSuccess) {
      if (!formValuesForPost.psSummaryHookResult.isPelaksanaanState) {
        router.replace(PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH)
      } else if (formValuesForPost.isLocked) {
        goBack()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formValues.isLocked,
    formValues.isSuccess,
    formValues.psSummaryHookResult.isPelaksanaanState,
    formValues.psSummaryHookResult.summaryFetchStatus,
    formValuesForPost.isLoading,
    formValuesForPost.isLocked,
    formValuesForPost.isSuccess,
    formValuesForPost.psSummaryHookResult.isPelaksanaanState,
  ])

  return (
    <>
      <div className="px-3 mx-auto max-w-7xl py-9 space-y-7">
        <Header
          title={
            <div className="flex items-center gap-x-3">
              Diskusi Tindak Lanjut
              {formValues.isViewOnly && !formValues.isAppeal && (
                <Badge variant="success">Selesai</Badge>
              )}
              {formValues.isAppeal && (
                <Badge variant="neutral">Perubahan sedang diajukan</Badge>
              )}
            </div>
          }
        />

        <OnceLoaded loaded={isSuccess}>
          {!formValuesForPost.isViewOnly ? (
            <CardForm
              formValues={formValues}
              formValuesForPost={formValuesForPost}
              onSubmitSuccess={() => goBack()}
            />
          ) : (
            <CardFormPreview formValues={formValues} />
          )}
        </OnceLoaded>
      </div>
    </>
  )
}

export default DiskusiTindakLanjut
